import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type ColumnDef } from "@tanstack/react-table";
import { ExternalLink, ListX, MessagesSquare, Plus, ScanSearch, Trash2, X } from "lucide-react";
import { ApiError } from "@/lib/api";
import { formatAccountNo, useAccounts } from "@/lib/accounts";
import {
  type OwnChat,
  accountOptionLabel,
  canAct,
  chatUrl,
  useDeleteOwnChat,
  useDiscoverOwnChats,
  useOwnChats,
} from "@/lib/ownChats";
import { formatNumber } from "@/lib/format";
import { CreateOwnChatDialog } from "@/components/ownchats/CreateOwnChatDialog";
import { OwnChatDrawer } from "@/components/ownchats/OwnChatDrawer";
import { HelpSection } from "@/components/HelpSection";
import { SecondAdminSetting } from "@/components/ownchats/SecondAdmin";
import { pushToast } from "@/components/Toaster";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ConfirmDialog,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ActionMenu, type ActionMenuItem } from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { SimpleSelect } from "@/components/ui/select";
import { DataTable } from "@/components/ui/table";

/**
 * Own chats: the groups and channels the company's accounts run. One is
 * created here from a chosen account, or taken in from what an account already
 * owns; its card then does what Telegram's own settings screen does.
 *
 * `?account=<id>` — the page as opened from an account's card: only that
 * account's chats, and "create" starts on it.
 */
export function OwnChatsPage() {
  const { t } = useTranslation();
  const chats = useOwnChats();
  const accounts = useAccounts();
  const del = useDeleteOwnChat();

  const [accountId, setAccountId] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get("account"),
  );
  useEffect(() => {
    const follow = () =>
      setAccountId(new URLSearchParams(window.location.search).get("account"));
    window.addEventListener("popstate", follow);
    return () => window.removeEventListener("popstate", follow);
  }, []);
  const [creating, setCreating] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleting, setDeleting] = useState<{ chat: OwnChat; inTelegram: boolean } | null>(null);

  const all = useMemo(() => chats.data ?? [], [chats.data]);
  const data = useMemo(
    () => (accountId ? all.filter((c) => c.account_id === accountId) : all),
    [all, accountId],
  );
  const account = accounts.data?.find((a) => a.id === accountId);
  const opened = all.find((c) => c.id === openId) ?? null;

  const showAll = () => {
    setAccountId(null);
    window.history.replaceState(null, "", "/own-chats");
  };

  const openChat = (id: string) => {
    setOpenId(id);
    setDrawerOpen(true);
  };

  const onDelete = async () => {
    if (!deleting) return;
    try {
      await del.mutateAsync({ id: deleting.chat.id, in_telegram: deleting.inTelegram });
      setDrawerOpen(false);
      pushToast({
        kind: "success",
        text: t(deleting.inTelegram ? "own_chats.deleted" : "own_chats.forgotten"),
      });
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("own_chats.action_error"),
      });
    }
  };

  const columns = useMemo<ColumnDef<OwnChat, unknown>[]>(
    () => [
      {
        id: "title",
        header: t("own_chats.col.title"),
        accessorFn: (c) => c.title,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="min-w-0">
              <span className="block truncate font-semibold text-fg-primary">{c.title}</span>
              <span className="block truncate text-xs text-fg-muted">
                {/* The "kind" column is hidden on a phone: say it here. */}
                <span className="sm:hidden">{t(`own_chats.kind.${c.kind}`)} · </span>
                {c.username ? `@${c.username}` : t("own_chats.access.closed_short")}
              </span>
            </div>
          );
        },
      },
      {
        id: "kind",
        header: t("own_chats.col.kind"),
        meta: { className: "hidden sm:table-cell" },
        accessorFn: (c) => c.kind,
        cell: ({ row }) => (
          <Badge tone={row.original.kind === "channel" ? "accent" : "info"}>
            {t(`own_chats.kind.${row.original.kind}`)}
          </Badge>
        ),
      },
      {
        id: "account",
        header: t("own_chats.col.account"),
        accessorFn: (c) => c.account_seq_no,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="min-w-0 text-xs text-fg-muted">
              <div className="truncate text-sm text-fg-primary">
                <span className="font-mono text-fg-muted">
                  {formatAccountNo(c.account_seq_no)}
                </span>{" "}
                <span className="hidden sm:inline">{c.account_label}</span>
              </div>
              <div>{t(c.is_creator ? "own_chats.role.owner" : "own_chats.role.admin")}</div>
            </div>
          );
        },
      },
      {
        id: "members",
        header: t("own_chats.col.members"),
        // Least urgent on a narrow screen; the card has the same number.
        meta: { className: "hidden min-[900px]:table-cell" },
        accessorFn: (c) => c.members_count ?? -1,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="text-xs text-fg-muted">
              <div className="font-mono text-sm tabular-nums text-fg-primary">
                {c.members_count !== null ? formatNumber(c.members_count) : "—"}
              </div>
              {c.requests_pending > 0 && (
                <div className="text-warning">
                  {t("own_chats.col.requests", { count: c.requests_pending })}
                </div>
              )}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: () => <span className="sr-only">{t("own_chats.actions")}</span>,
        enableSorting: false,
        cell: ({ row }) => {
          const c = row.original;
          const url = chatUrl(c);
          const items: ActionMenuItem[] = [];
          if (url) {
            items.push({
              label: t("own_chats.open_in_telegram"),
              icon: <ExternalLink size={15} strokeWidth={2} aria-hidden="true" />,
              onSelect: () => window.open(url, "_blank", "noreferrer"),
            });
          }
          items.push({
            label: t("own_chats.forget"),
            icon: <ListX size={15} strokeWidth={2} aria-hidden="true" />,
            onSelect: () => setDeleting({ chat: c, inTelegram: false }),
            separatorBefore: items.length > 0,
          });
          if (c.is_creator) {
            items.push({
              label: t("own_chats.delete"),
              icon: <Trash2 size={15} strokeWidth={2} aria-hidden="true" />,
              onSelect: () => setDeleting({ chat: c, inTelegram: true }),
              destructive: true,
            });
          }
          return (
            <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
              <ActionMenu label={t("own_chats.actions")} items={items} />
            </div>
          );
        },
      },
    ],
    [t],
  );

  const subtitle = useMemo(() => {
    if (all.length === 0) return t("own_chats.subtitle");
    return t("own_chats.subtitle_counts", {
      channels: all.filter((c) => c.kind === "channel").length,
      groups: all.filter((c) => c.kind === "group").length,
    });
  }, [all, t]);

  const createButton = (
    <Button
      onClick={() => setCreating(true)}
      iconLeft={<Plus size={16} strokeWidth={2} aria-hidden="true" />}
    >
      {t("own_chats.create.open")}
    </Button>
  );
  const discoverButton = (
    <Button
      variant="outline"
      onClick={() => setDiscovering(true)}
      iconLeft={<ScanSearch size={16} strokeWidth={2} aria-hidden="true" />}
    >
      {t("own_chats.discover.open")}
    </Button>
  );

  return (
    <section className="mx-auto max-w-7xl space-y-6 px-6 py-8">
      <PageHeader
        title={t("own_chats.title")}
        subtitle={subtitle}
        actions={
          <>
            {discoverButton}
            {createButton}
          </>
        }
      />

      {accountId && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-fg-secondary">
          <span>
            {t("own_chats.filtered_by", {
              account: account
                ? `${formatAccountNo(account.seq_no)} · ${account.display_name}`
                : "…",
            })}
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={showAll}
            iconLeft={<X size={14} strokeWidth={2} aria-hidden="true" />}
          >
            {t("own_chats.show_all")}
          </Button>
        </div>
      )}

      {chats.isLoading && <p className="text-fg-muted">{t("own_chats.loading")}</p>}
      {chats.isError && <p className="text-danger">{t("own_chats.load_error")}</p>}

      {chats.data && (
        <DataTable
          columns={columns}
          data={data}
          getRowId={(c) => c.id}
          onRowClick={(c) => openChat(c.id)}
          empty={
            <EmptyState
              icon={MessagesSquare}
              title={t(accountId ? "own_chats.empty_title_account" : "own_chats.empty_title")}
              description={t("own_chats.empty_hint")}
              action={createButton}
              secondaryAction={discoverButton}
            />
          }
        />
      )}

      <SecondAdminSetting />

      <HelpSection
        i18nKey="own_chats.help"
        paragraphs={4}
        list={{
          titleKey: "own_chats.help.list_title",
          itemsKey: "own_chats.help.list_items",
          itemCount: 5,
        }}
      />

      <CreateOwnChatDialog
        open={creating}
        onOpenChange={setCreating}
        accountId={accountId}
        onCreated={openChat}
      />
      <DiscoverDialog open={discovering} onOpenChange={setDiscovering} accountId={accountId} />

      <OwnChatDrawer
        key={opened?.id ?? "none"}
        chat={opened}
        chats={all}
        open={drawerOpen && opened !== null}
        onOpenChange={setDrawerOpen}
        onDelete={(chat, inTelegram) => setDeleting({ chat, inTelegram })}
      />

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        tone="danger"
        title={t(deleting?.inTelegram ? "own_chats.delete_confirm" : "own_chats.forget_confirm", {
          name: deleting?.chat.title ?? "",
        })}
        description={t(
          deleting?.inTelegram
            ? `own_chats.delete_confirm_hint_${deleting.chat.kind}`
            : "own_chats.forget_confirm_hint",
        )}
        confirmText={t(deleting?.inTelegram ? "own_chats.delete" : "own_chats.forget")}
        cancelText={t("common.cancel")}
        onConfirm={onDelete}
      />
    </section>
  );
}

/**
 * Take in the groups and channels an account already owns or administers —
 * made in Telegram by hand, or before the account was bought.
 */
function DiscoverDialog({
  open,
  onOpenChange,
  accountId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string | null;
}) {
  const { t } = useTranslation();
  const accounts = useAccounts();
  const discover = useDiscoverOwnChats();
  const [account, setAccount] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setAccount(accountId ?? "");
    setError(null);
  }, [open, accountId]);

  const usable = useMemo(
    () => (accounts.data ?? []).filter(canAct).sort((a, b) => a.seq_no - b.seq_no),
    [accounts.data],
  );
  const chosen = usable.find((a) => a.id === account);

  const submit = async () => {
    if (!chosen) return;
    setError(null);
    try {
      const out = await discover.mutateAsync(chosen.id);
      pushToast({
        kind: out.added > 0 ? "success" : "info",
        text:
          out.found === 0
            ? t("own_chats.discover.none")
            : t("own_chats.discover.done", { found: out.found, added: out.added }),
      });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("own_chats.action_error"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next || !discover.isPending) && onOpenChange(next)}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{t("own_chats.discover.title")}</DialogTitle>
          <DialogDescription>{t("own_chats.discover.subtitle")}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <SimpleSelect
            value={chosen ? account : ""}
            onValueChange={setAccount}
            placeholder={t("own_chats.field.account_placeholder") ?? undefined}
            disabled={accounts.isLoading}
            options={usable.map((a) => ({
              value: a.id,
              label: accountOptionLabel(a, t("own_chats.account_not_warmed")),
            }))}
          />
          {accounts.data && account !== "" && !chosen && (
            <p className="text-xs text-warning">{t("own_chats.account_cannot_act")}</p>
          )}
          {error && <Alert tone="danger">{error}</Alert>}
        </DialogBody>
        <DialogFooter>
          {discover.isPending && (
            <span className="mr-auto text-xs text-fg-muted">{t("own_chats.reading")}</span>
          )}
          <Button
            variant="outline"
            disabled={discover.isPending}
            onClick={() => onOpenChange(false)}
          >
            {t("common.cancel")}
          </Button>
          <Button
            disabled={!chosen || discover.isPending}
            loading={discover.isPending}
            onClick={submit}
          >
            {t("own_chats.discover.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
